import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  Target,
  ShieldCheck,
  Building2,
  Users2,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AmbientParticles from "@/components/site/AmbientParticles";
import StandardCTA from "@/components/site/StandardCTA";
import { COMPANY_IMAGES } from "@/lib/site";

const VALUES = [
  {
    icon: Brain,
    title: "Intelligence in Everything",
    desc: "Every decision powered by data, not guesswork.",
  },
  {
    icon: Target,
    title: "Precision Over Noise",
    desc: "Sharp, specific, built for outcomes.",
  },
  {
    icon: ShieldCheck,
    title: "Trust by Design",
    desc: "Enterprise-grade security and integrity at the core.",
  },
  {
    icon: Building2,
    title: "Built for Real Estate",
    desc: "Purpose-built for how the industry actually works.",
  },
  {
    icon: Users2,
    title: "Operator-First",
    desc: "Made by operators, for operators.",
  },
];

export default function CompanyPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setScrollY(window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  const heroParallax = `translate3d(0, ${-scrollY * 0.14}px, 0)`;
  const visionParallax = `translate3d(0, ${scrollY * 0.06}px, 0)`;
  const ctaParallax = `translate3d(0, ${scrollY * 0.04}px, 0)`;

  return (
    <div
      className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden"
      data-testid="company-page"
    >
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <AmbientParticles count={32} tone="mixed" density={0.55} />
      </div>
      <Navbar />

      {/* ============ HERO ============ */}
      <section className="relative pt-[120px] lg:pt-[150px] pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-grid opacity-[0.06]" aria-hidden />
        <div
          className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, rgba(96,165,250,0.20), transparent)",
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-center min-h-[560px] lg:min-h-[660px]">
            {/* Left — copy */}
            <div className="fade-up relative z-10">
              <div className="eyebrow-positioning">ABOUT ESTHERA
              </div>
              <h1 className="font-display mt-5 text-platinum text-[44px] sm:text-[56px] lg:text-[64px] xl:text-[68px] leading-[1.04] tracking-[-0.022em]">
                Building the{" "}
                <span
                  className="text-sapphire-glow"
                  style={{ textShadow: "0 0 36px rgba(96,165,250,0.40)" }}
                >
                  Intelligence Layer
                </span>{" "}
                for Real Estate.
              </h1>
              <p className="mt-6 text-[16px] sm:text-[17px] text-platinum/72 leading-[1.7] max-w-[540px]">
                Esthera RealityOS unifies data, automates operations, and brings
                real intelligence to how real estate works.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => navigate("/book-demo")}
                  data-testid="company-hero-book-demo"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                >
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate("/contact")}
                  data-testid="company-hero-contact"
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                >
                  Get in Touch <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Right — full-bleed hero asset (Homepage style) */}
            <div
              className="relative lg:-mr-10 xl:-mr-20"
              data-testid="company-hero-visual"
              style={{ transform: heroParallax, willChange: "transform" }}
            >
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 55% at 55% 50%, rgba(37,99,235,0.32), transparent 72%)",
                  filter: "blur(48px)",
                }}
                aria-hidden
              />
              <div className="relative">
                <img
                  src={COMPANY_IMAGES.hero}
                  alt="Cinematic real estate skyline wired into the Esthera intelligence network"
                  className="block w-full h-[520px] lg:h-[680px] object-cover object-center"
                  style={{
                    WebkitMaskImage:
                      "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                    maskImage:
                      "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                  }}
                  draggable="false"
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(10,15,26,0.75) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.98) 100%)",
                  }}
                  aria-hidden
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(10,15,26,1) 0%, rgba(10,15,26,0.82) 14%, rgba(10,15,26,0.18) 32%, rgba(10,15,26,0) 50%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)",
                  }}
                  aria-hidden
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHY WE EXIST + OUR APPROACH ============ */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
            {/* Why We Exist */}
            <div
              className="relative rounded-2xl border border-white/[0.07] p-8 lg:p-10 fade-up overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(13,18,32,0.6) 0%, rgba(10,15,26,0.92) 100%)",
              }}
            >
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/40 to-transparent" />
              <div
                className="text-[10.5px] tracking-[0.34em] uppercase font-medium text-amber-300/85 mb-4"
                style={{ textShadow: "0 0 12px rgba(217,165,79,0.45)" }}
              >
                WHY WE EXIST
              </div>
              <h2 className="font-display text-platinum text-[26px] sm:text-[30px] lg:text-[34px] leading-[1.15] tracking-[-0.018em]">
                Real estate runs on{" "}
                <span className="text-platinum/55">
                  disconnected systems, manual workflows,
                </span>{" "}
                and reactive decisions.
              </h2>
              <p className="mt-5 text-[14.5px] text-platinum/70 leading-[1.75] luxe-dropcap">
                For decades, the industry has been forced to operate on tools
                that were never built for it. Data lives in silos. Teams burn
                hours on repetitive work. Critical decisions are made in
                hindsight, not in real time.
              </p>
              <p className="mt-4 text-[14.5px] text-platinum/70 leading-[1.75]">
                We started Esthera to end that. To replace the patchwork with{" "}
                <span className="text-sapphire-glow">one intelligent layer</span>{" "}
                — purpose-built for how real estate actually works.
              </p>
            </div>

            {/* Our Approach */}
            <div
              className="relative rounded-2xl border border-white/[0.07] p-8 lg:p-10 fade-up overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(13,18,32,0.6) 0%, rgba(10,15,26,0.92) 100%)",
                animationDelay: "100ms",
              }}
            >
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/40 to-transparent" />
              <div
                className="text-[10.5px] tracking-[0.34em] uppercase font-medium text-sapphire-glow mb-4"
                style={{ textShadow: "0 0 12px rgba(96,165,250,0.55)" }}
              >
                OUR APPROACH
              </div>
              <h2 className="font-display text-platinum text-[26px] sm:text-[30px] lg:text-[34px] leading-[1.15] tracking-[-0.018em]">
                Purpose-built{" "}
                <span
                  className="text-sapphire-glow"
                  style={{ textShadow: "0 0 24px rgba(96,165,250,0.40)" }}
                >
                  AI for real estate
                </span>{" "}
                — unifying data, teams, and decisions.
              </h2>
              <p className="mt-5 text-[14.5px] text-platinum/70 leading-[1.75]">
                We don't bolt AI onto legacy software. We rebuild the operating
                model from the ground up — uniting every workflow, every team,
                and every decision on one continuously learning intelligence
                layer.
              </p>
              <p className="mt-4 text-[14.5px] text-platinum/70 leading-[1.75]">
                The result: leaders move with clarity, operators move with
                speed, and the entire business compounds on every signal it
                produces.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ LEADERSHIP ============ */}
      <section className="relative pb-20 lg:pb-24" id="leadership">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <h2 className="font-display text-center text-platinum text-[28px] sm:text-[34px] lg:text-[40px] leading-tight tracking-[-0.018em] fade-up">
            Leadership
          </h2>
          <p className="mt-4 text-center text-[14.5px] text-platinum/60 max-w-[560px] mx-auto fade-up">
            Built by operators who have lived inside the systems we're
            replacing.
          </p>

          <div
            className="relative mt-12 rounded-2xl border border-white/[0.07] overflow-hidden fade-up"
            style={{
              background:
                "linear-gradient(180deg, rgba(13,18,32,0.6) 0%, rgba(10,15,26,0.94) 100%)",
            }}
          >
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />
            <div className="grid lg:grid-cols-[0.95fr_1.25fr] gap-0 items-stretch">
              {/* Founder portrait — full-bleed, masked */}
              <div className="relative min-h-[460px] lg:min-h-[560px] overflow-hidden">
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(55% 55% at 50% 45%, rgba(37,99,235,0.30), transparent 75%)",
                    filter: "blur(40px)",
                  }}
                  aria-hidden
                />
                <img
                  src={COMPANY_IMAGES.founder}
                  alt="Nishq Rajaram — Founder of Esthera RealityOS"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                  style={{
                    filter:
                      "brightness(0.95) contrast(1.10) saturate(1.02)",
                    WebkitMaskImage:
                      "radial-gradient(110% 105% at 50% 45%, #000 58%, rgba(0,0,0,0.55) 82%, transparent 100%)",
                    maskImage:
                      "radial-gradient(110% 105% at 50% 45%, #000 58%, rgba(0,0,0,0.55) 82%, transparent 100%)",
                  }}
                  draggable="false"
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(10,15,26,0.35) 0%, rgba(10,15,26,0) 30%, rgba(10,15,26,0) 60%, rgba(10,15,26,0.92) 100%)",
                  }}
                  aria-hidden
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(10,15,26,0.10) 0%, rgba(10,15,26,0) 35%, rgba(10,15,26,0) 75%, rgba(10,15,26,0.85) 100%)",
                  }}
                  aria-hidden
                />
              </div>

              {/* Bio side */}
              <div className="relative p-7 lg:p-12 flex flex-col justify-center">
                {/* Subtle sapphire halo accent */}
                <div
                  className="absolute -right-20 -top-20 w-[460px] h-[460px] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(closest-side, rgba(96,165,250,0.18), transparent 70%)",
                    filter: "blur(48px)",
                  }}
                  aria-hidden
                />

                <div className="relative z-10">
                  <div className="eyebrow-intelligence">FOUNDER</div>
                  <h3 className="font-display mt-4 text-platinum text-[30px] sm:text-[36px] lg:text-[40px] leading-[1.08] tracking-[-0.018em]">
                    Nishq Rajaram
                  </h3>
                  <div className="mt-2 text-[13px] text-platinum/55 tracking-[0.10em] uppercase">
                    Founder &amp; CEO — Esthera RealityOS
                  </div>

                  <div className="mt-6 space-y-4 text-[14.5px] text-platinum/72 leading-[1.75] max-w-[560px]">
                    <p className="luxe-dropcap">
                      Nishq founded Esthera after seeing the same pattern across real estate: high-value inquiries lost to slow follow-up, disconnected tools, and decisions made in hindsight. While the industry kept buying software that was never built for how it actually operates, the core problem — fragmented operations quietly leaking revenue — went unsolved.
                    </p>
                    <p>
                      He built Esthera to close that gap:{" "}
                      <span className="text-sapphire-glow">one intelligent layer</span>{" "}
                      that unifies data, automates the busywork, and gives operators the clarity to act while it still matters.
                    </p>
                    <p>
                      Esthera is built <span className="text-sapphire-glow">operator-first</span> — designed around how real estate teams actually work, not how software vendors assume they do.
                    </p>
                  </div>

                  <div className="mt-8 inline-flex items-center gap-3 rounded-full px-4 py-2 border border-white/[0.08]"
                    style={{
                      background: "rgba(15,22,40,0.55)",
                      boxShadow:
                        "0 0 18px -6px rgba(96,165,250,0.30), inset 0 1px 0 rgba(255,255,255,0.04)",
                    }}>
                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-platinum/55">
                      A Product Of
                    </span>
                    <span className="text-[12px] tracking-[0.24em] uppercase font-medium text-platinum"
                      style={{ textShadow: "0 0 12px rgba(229,231,235,0.20)" }}>
                      Esthera Ventures Global
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOW WE OPERATE ============ */}
      <section className="relative pt-8 lg:pt-12 pb-20 lg:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="text-center fade-up">
            <div className="eyebrow-positioning">HOW WE OPERATE
            </div>
            <h2 className="font-display mt-4 text-platinum text-[30px] sm:text-[36px] lg:text-[44px] leading-tight tracking-[-0.018em]">
              The principles that{" "}
              <span
                className="text-sapphire-glow"
                style={{ textShadow: "0 0 30px rgba(96,165,250,0.45)" }}
              >
                shape every decision.
              </span>
            </h2>
          </div>

          <div className="mt-14 lg:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`company-value-${i}`}
                  className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-7 fade-up overflow-hidden transition-all duration-500 hover:border-sapphire-glow/40"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(13,18,32,0.55) 0%, rgba(10,15,26,0.92) 100%)",
                    animationDelay: `${i * 70}ms`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 0 32px -8px rgba(96,165,250,0.40)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />
                  <div
                    className="w-12 h-12 rounded-full inline-flex items-center justify-center mb-5"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.14), #0E1422 75%)",
                      border: "1px solid rgba(96,165,250,0.55)",
                      boxShadow:
                        "0 0 16px -3px rgba(96,165,250,0.45)",
                    }}
                  >
                    <Icon className="w-5 h-5 text-sapphire-glow" />
                  </div>
                  <div className="font-display text-platinum text-[16px] leading-[1.22] tracking-tight">
                    {v.title}
                  </div>
                  <p className="mt-2.5 text-[12.5px] text-platinum/60 leading-[1.6]">
                    {v.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ WHERE WE'RE GOING — Vision band ============ */}
      <section className="relative pb-24 lg:pb-24 overflow-hidden">
        {/* Full-bleed vision band asset (Homepage style — escapes the column grid) */}
        <div
          className="absolute inset-x-0 top-0 bottom-16 sm:bottom-12 lg:bottom-0 pointer-events-none"
          style={{ transform: visionParallax, willChange: "transform" }}
          aria-hidden
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(70% 60% at 50% 50%, rgba(37,99,235,0.34), transparent 78%)",
              filter: "blur(60px)",
            }}
          />
          <img
            src={COMPANY_IMAGES.visionBand}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{
              mixBlendMode: "screen",
              filter: "brightness(0.85) contrast(1.55) saturate(1.15)",
              WebkitMaskImage:
                "radial-gradient(120% 100% at 50% 50%, #000 50%, rgba(0,0,0,0.55) 78%, transparent 100%)",
              maskImage:
                "radial-gradient(120% 100% at 50% 50%, #000 50%, rgba(0,0,0,0.55) 78%, transparent 100%)",
            }}
            draggable="false"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,15,26,0.95) 0%, rgba(10,15,26,0.50) 25%, rgba(10,15,26,0.55) 68%, rgba(10,15,26,1) 100%)",
            }}
          />
        </div>

        {/* Solid bottom cap — locks the section boundary so glows don't bleed into the CTA below (esp. on mobile) */}
        <div className="absolute inset-x-0 bottom-0 h-16 sm:h-12 lg:h-0 pointer-events-none bg-obsidian" aria-hidden />

        <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10 py-20 lg:py-28 text-center">
          <div
            className="text-[10.5px] tracking-[0.38em] uppercase font-medium text-sapphire-glow fade-up"
            style={{ textShadow: "0 0 14px rgba(96,165,250,0.55)" }}
          >
            WHERE WE'RE GOING
          </div>
          <h2 className="font-display mt-5 text-platinum text-[30px] sm:text-[40px] lg:text-[52px] leading-[1.08] tracking-[-0.020em] fade-up">
            We're building the{" "}
            <span
              className="text-sapphire-glow"
              style={{ textShadow: "0 0 36px rgba(96,165,250,0.45)" }}
            >
              intelligent operating system
            </span>{" "}
            real estate runs on.
          </h2>
          <p className="mt-7 max-w-[760px] mx-auto text-[15px] sm:text-[16px] text-platinum/72 leading-[1.75] fade-up">
            Unifying every workflow, every team, and every decision under one
            continuously learning layer of intelligence — so the world's most
            important asset class operates with the clarity, speed, and scale
            it deserves.
          </p>
        </div>
      </section>

      {/* ============ FINAL CTA — uses StandardCTA (uniform across site) ============ */}
      <StandardCTA
        testId="company-cta"
        eyebrow="GET STARTED"
        eyebrowTone="positioning"
        title="Ready to transform your real estate operations?"
        highlight="real estate operations?"
        subtitle="See how Esthera RealityOS becomes the single intelligence layer across your data, your teams, and your decisions."
        crystal={COMPANY_IMAGES.ctaCrystal}
        primaryLabel="Book a Demo"
        primaryHref="/book-demo"
        secondaryLabel="Get in Touch"
        secondaryHref="/contact"
      />

      <Footer />
    </div>
  );
}
